"use strict";
var __createBinding = (this && this.__createBinding) || (Object.create ? (function(o, m, k, k2) {
    if (k2 === undefined) k2 = k;
    var desc = Object.getOwnPropertyDescriptor(m, k);
    if (!desc || ("get" in desc ? !m.__esModule : desc.writable || desc.configurable)) {
      desc = { enumerable: true, get: function() { return m[k]; } };
    }
    Object.defineProperty(o, k2, desc);
}) : (function(o, m, k, k2) {
    if (k2 === undefined) k2 = k;
    o[k2] = m[k];
}));
var __setModuleDefault = (this && this.__setModuleDefault) || (Object.create ? (function(o, v) {
    Object.defineProperty(o, "default", { enumerable: true, value: v });
}) : function(o, v) {
    o["default"] = v;
});
var __importStar = (this && this.__importStar) || function (mod) {
    if (mod && mod.__esModule) return mod;
    var result = {};
    if (mod != null) for (var k in mod) if (k !== "default" && Object.prototype.hasOwnProperty.call(mod, k)) __createBinding(result, mod, k);
    __setModuleDefault(result, mod);
    return result;
};
var __exportStar = (this && this.__exportStar) || function(m, exports) {
    for (var p in m) if (p !== "default" && !Object.prototype.hasOwnProperty.call(exports, p)) __createBinding(exports, m, p);
};
var __importDefault = (this && this.__importDefault) || function (mod) {
    return (mod && mod.__esModule) ? mod : { "default": mod };
};
Object.defineProperty(exports, "__esModule", { value: true });
exports.METHOD = exports.apiHtmlResponse = exports.apiTextResponse = exports.apiJsonResponse = exports.makeGetRequestResponder = exports.renderAnyRouteUrl = exports.makeRenderRouteUrl = exports.makeCreateRoute = void 0;
const pathToRegexp = __importStar(require("path-to-regexp"));
const query_string_1 = __importDefault(require("query-string"));
const helpers_1 = require("../misc/helpers");
const console_1 = require("../services/logger/console");
/**
 * Makes a createRoute function that can be used to create routes (this is a factory factory). The
 * `makeCreateRoute` function is typically called once in the backend and once in the frontend to
 * set the types for the resulting `createRoute` function -- that latter function is called once
 * per route. E.g. for the backend, the call could look like:
 *
 * ```
 * export const createRoute = makeCreateRoute<AppServices, ApiRouteRequest, {
 *   method: METHOD;
 * }>();
 * ```
 *
 * Notes:
 * * The `{method: METHOD}` defines the `Ex` extension type; here, the `method` property is only
 *   relevant to backend routes.
 * * when defining the `createRoute` method, only the request input format is defined, the output
 *   format is derived from the routes.
 *
 * When calling the resulting `createRoute` function, the only required params of the route are the
 * name, path, and handler. Other params can be added to the type and then later used in the
 * routeMatcher.
 *
 * eg when defining requestServiceProvider in line, the types have a hard time, it helps to put in another argument:
 * ```
 * export const exampleRoute = createRoute({
 *   name: 'exampleRoute', method: METHOD.GET, path: '/api/example/:key',
 *   requestServiceProvider: composeServiceMiddleware({
 *     cookieAuthMiddleware,
 *     documentStoreMiddleware,
 *   }},
 *   async(params: {key: string}, services) => {
 *     const result = await services.myDocumentStore.getItem(params.key);
 *
 *     if (!result) {
 *       throw new NotFoundError('requested item not found');
 *     }
 *
 *     return apiJsonResponse(200, result);
 *   }
 * );
 * ```
 * eg when using a pre-existing provider variable the types work better:
 * ```
 * export const exampleRoute = createRoute({
 *   name: 'exampleRoute', method: METHOD.GET, path: '/api/example/:key',
 *   requestServiceProvider,
 *   handler: async(params: {key: string}, services) => {
 *     const result = await services.myDocumentStore.getItem(params.key);
 *
 *     if (!result) {
 *       throw new NotFoundError('requested item not found');
 *     }
 *
 *     return apiJsonResponse(200, result);
 *   }
 * });
 * ```
 */
const makeCreateRoute = () => (...args) => {
    return (args.length === 1
        ? args[0]
        : { ...args[0], handler: args[1] });
};
exports.makeCreateRoute = makeCreateRoute;
/* begin reverse routing utils */
/**
 * Makes a renderRouteUrl function that can be used to render route paths (this is a factory
 * factory). The returned function takes a `route`, `params`, and `query` and returns a string
 * with the params and query substituted into the route path.
 *
 * this function is initialized using the Ru type which indicates the specific routes wired into
 * the application, which means that if you try to build a url with a route which is not wired
 * into the router you will get a type error. this is a feature to prevent referencing routes that
 * don't exist or aren't handling requests properly.
 *
 * if you are making a helper function or need to render a route outside your application, you
 * can use the `renderAnyRouteUrl` function
 */
const makeRenderRouteUrl = () => (route, params, query = {}) => {
    const getPathForParams = pathToRegexp.compile(route.path, { encode: encodeURIComponent });
    const search = query_string_1.default.stringify(query);
    const path = getPathForParams(params) + (search ? `?${search}` : '');
    return path;
};
exports.makeRenderRouteUrl = makeRenderRouteUrl;
/**
 * A pre-made result from `makeRenderRouteUrl`, this function interpolates parameter and query
 * arguments into a route path.
 *
 * prefer using `renderRouteUrl` initialized with your applications route union
 * when possible to help catch improperly initialized routes.
 *
 * @param route the route that has a `path` to be interpolated
 * @param params the parameters to interpolate into the route path
 * @param query the query parameters to add to the route path
 * @returns the interpolated route path
 */
exports.renderAnyRouteUrl = (0, exports.makeRenderRouteUrl)();
const bindRoute = (services, appBinder, pathExtractor, matcher) => (route) => {
    const getParamsFromPath = pathToRegexp.match(route.path, { decode: decodeURIComponent });
    const boundServiceProvider = route.requestServiceProvider && appBinder(services, route.requestServiceProvider);
    return (request, logger) => {
        const path = pathExtractor(request);
        const match = getParamsFromPath(path);
        if ((!matcher || matcher(request, route)) && match) {
            return () => route.handler(match.params, boundServiceProvider ? boundServiceProvider({ request, logger }, { route, params: match.params }) : undefined);
        }
    };
};
/**
 * A factory factory for creating request responders (functions that take a request and return a
 * response -- these functions let us implement Lambda `handler` functions).
 *
 * Use it in two steps. First, call it with the general business logic that defines routes, logs,
 * errors, etc:
 * ```
 * const getRequestResponder = makeGetRequestResponder<
 *    AppServices, TRoutes, ApiRouteRequest, Promise<ApiRouteResponse>
 *   >()  // this empty invocation helps typescript mix defined and inferred types
 *   ({
 *     routes: apiRoutes, // the route definitions
 *     pathExtractor,     // how to get the path out of the request format
 *     routeMatcher,      // logic for matching route (beyond path matching, optional)
 *     errorHandler,      // any special error handling
 *   });
 * ```
 * Note here that among other things we're specifying a generic response format that the response
 * and error handling middleware can use, if any routes have responses that don't adhere to this
 * it'll complain about it.
 *
 * Next, we use the `getRequestResponder` to create a responder for a specific lambda entrypoint:
 * ```
 * export const handler: (request: APIGatewayProxyEventV2): Promise<ApiRouteResponse> =>
 *   getRequestResponder(
 *     lambdaServices,  // the AppServices for this entrypoint
 *     lambdaMiddleware // environment specific response middleware (like cors)
 *   );
 * ```
 */
const makeGetRequestResponder = () => ({ routes, pathExtractor, routeMatcher, errorHandler, logExtractor }) => (services, responseMiddleware) => {
    const appBinderImpl = (app, middleware) => middleware(app, appBinder);
    const appBinder = (0, helpers_1.memoize)(appBinderImpl);
    const boundRoutes = routes().map(bindRoute(services, appBinder, pathExtractor, routeMatcher));
    const boundResponseMiddleware = responseMiddleware ? responseMiddleware(services) : undefined;
    const appLogger = services.logger || (0, console_1.createConsoleLogger)();
    // *note* this opaque promise guard is less generic than i hoped so
    //   i'm leaving it here instead of the guards file.
    //
    // its less than ideal because it enforces that the handlers return
    // the same type as the parent promise, usually a handler can be either a
    // promise or a non-promise value and the promise figures it out, but those
    // types are getting complicated quickly here.
    const isPromise = (thing) => thing instanceof Promise;
    return (request) => {
        const logger = appLogger.createSubContext();
        if (logExtractor) {
            logger.setContext(logExtractor(request));
        }
        try {
            const executor = (0, helpers_1.mapFind)(boundRoutes, (route) => route(request, logger));
            if (executor) {
                const result = boundResponseMiddleware ?
                    boundResponseMiddleware(executor(), { request, logger }) : executor();
                if (isPromise(result) && errorHandler) {
                    const errorHandlerWithMiddleware = (e) => boundResponseMiddleware ?
                        boundResponseMiddleware(errorHandler(e, logger), { request, logger }) : errorHandler(e, logger);
                    return result.catch(errorHandlerWithMiddleware);
                }
                else {
                    return result;
                }
            }
            else if (boundResponseMiddleware) {
                return boundResponseMiddleware(undefined, { request, logger });
            }
        }
        catch (e) {
            if (errorHandler && e instanceof Error) {
                return boundResponseMiddleware
                    ? boundResponseMiddleware(errorHandler(e, logger), { request, logger })
                    : errorHandler(e, logger);
            }
            throw e;
        }
        return undefined;
    };
};
exports.makeGetRequestResponder = makeGetRequestResponder;
/**
 * Returns a JSON response. Handles serializing the data to JSON and setting the content-type header.
 * @param statusCode e.g. 201
 * @param data the object to be serialized to JSON
 * @param headers HTTP headers
 * @example
 * return apiJsonResponse(
 *   200, {
 *     message: "hello, world!",
 *     foo: "bar",
 *   },
 *   { 'X-Frame-Options': 'DENY' }
 * );
 */
const apiJsonResponse = (statusCode, data, headers) => ({ statusCode, data, body: JSON.stringify(data), headers: { ...headers, 'content-type': 'application/json' } });
exports.apiJsonResponse = apiJsonResponse;
/**
 * Returns a plain text response. Handles setting the content-type header.
 * @param statusCode e.g. 201
 * @param data some string
 * @param headers HTTP headers
 * @example return apiTextResponse(200, 'some text')
 */
const apiTextResponse = (statusCode, data, headers) => ({ statusCode, data, body: data, headers: { ...headers, 'content-type': 'text/plain' } });
exports.apiTextResponse = apiTextResponse;
/**
 * Returns an HTML response. Handles setting the content-type header.
 * @param statusCode e.g. 201
 * @param data some string
 * @param headers HTTP headers
 * @example return apiHtmlResponse(200, '<b>some text</b>')
 */
const apiHtmlResponse = (statusCode, data, headers) => ({ statusCode, data, body: data, headers: { ...headers, 'content-type': 'text/html' } });
exports.apiHtmlResponse = apiHtmlResponse;
/** HTTP method enum */
var METHOD;
(function (METHOD) {
    METHOD["GET"] = "GET";
    METHOD["HEAD"] = "HEAD";
    METHOD["POST"] = "POST";
    METHOD["PUT"] = "PUT";
    METHOD["PATCH"] = "PATCH";
    METHOD["DELETE"] = "DELETE";
    METHOD["OPTIONS"] = "OPTIONS";
})(METHOD = exports.METHOD || (exports.METHOD = {}));
__exportStar(require("./helpers"), exports);
